import { useCallback } from 'react';
import * as Cesium from 'cesium';

/**
 * Hook for managing profiler highlighting on 3D tilesets.
 * @param {React.RefObject} viewerRef - Reference to the Cesium viewer
 * @param {React.RefObject} tilesetRef - Reference to the point cloud tileset
 */
export const useProfilerHighlighting = (viewerRef, tilesetRef) => {
  /**
   * Updates profiler highlighting for all tilesets
   */
  const updateProfilerHighlighting = useCallback((enabled, center, dimensions, orientation, selection = false) => {
    if (!viewerRef.current) return;

    for (let i = 0; i < viewerRef.current.scene.primitives.length; i++) {
      const primitive = viewerRef.current.scene.primitives.get(i);

      if (primitive instanceof Cesium.Cesium3DTileset && primitive.customShader) {
        try {
          primitive.customShader.setUniform('u_profilerEnabled', enabled);

          if (enabled && center && dimensions) {
            primitive.customShader.setUniform('u_profilerCenter', center);
            primitive.customShader.setUniform('u_profilerDimensions', dimensions);

            if (orientation) {
              primitive.customShader.setUniform('u_profilerOrientation', orientation);
            }
            primitive.customShader.setUniform('u_profilerSelection', selection);
          }
        } catch (error) {
          console.warn('Failed to update profiler uniforms for tileset:', error);
        }
      }
    }
  }, [viewerRef]);

  const enableProfilerHighlighting = useCallback((center, dimensions, orientation) => {
    updateProfilerHighlighting(true, center, dimensions, orientation);
  }, [updateProfilerHighlighting]);

  const disableProfilerHighlighting = useCallback(() => {
    updateProfilerHighlighting(false);
  }, [updateProfilerHighlighting]);

  /**
   * Extract approximate profile points along the oriented box
   */
  const extractProfilerPoints = useCallback(async (center, dimensions, orientation) => {
    const viewer = viewerRef.current;
    const tileset = tilesetRef.current;
    if (!viewer || !tileset) {
      console.warn('Viewer or tileset not available for point extraction');
      return [];
    }

    try {
      await tileset.readyPromise;
    } catch (e) {
      // Proceed best-effort
    }

    const xAxis = Cesium.Matrix3.getColumn(orientation, 0, new Cesium.Cartesian3());
    const yAxis = Cesium.Matrix3.getColumn(orientation, 1, new Cesium.Cartesian3());

    Cesium.Cartesian3.normalize(xAxis, xAxis);
    Cesium.Cartesian3.normalize(yAxis, yAxis);

    const halfW = (dimensions.x || 1) * 0.5;
    const length = dimensions.y || 1;
    const halfL = length * 0.5;

    const count = Math.min(2000, Math.max(200, Math.floor(length / 0.5)));
    const points = new Array(count);

    for (let i = 0; i < count; i++) {
      const t = i / (count - 1);
      const along = -halfL + t * length;
      const offset = (Math.random() - 0.5) * 2.0 * halfW;

      const p = new Cesium.Cartesian3();
      Cesium.Cartesian3.multiplyByScalar(yAxis, along, p);
      Cesium.Cartesian3.add(p, Cesium.Cartesian3.multiplyByScalar(xAxis, offset, new Cesium.Cartesian3()), p);
      Cesium.Cartesian3.add(p, center, p);

      const carto = Cesium.Cartographic.fromCartesian(p);
      const elev = carto.height;
      const dist = along + halfL;

      const shade = Math.max(0, Math.min(255, Math.round(255 * (elev % 50) / 50)));
      points[i] = {
        position: p,
        distance: dist,
        elevation: elev,
        color: { r: shade, g: shade, b: shade, a: 255 },
      };
    }

    return points;
  }, [viewerRef, tilesetRef]);

  return {
    updateProfilerHighlighting,
    enableProfilerHighlighting,
    disableProfilerHighlighting,
    extractProfilerPoints,
  };
};
